import React from 'react';
import { keepPreviousData } from '@tanstack/react-query';

import { HomepageIntroDesktop, HomepageIntroMobile, HomepageSlots } from 'components/homepage';
import { OrderFeedbackReminderModalManager } from 'components/layout';
import { Snowfall } from 'components/seasonal';
import { Responsive } from 'components/ui';
import { useHomepage } from 'hooks/data/useHomepage';
import { useIsAuthenticated } from 'hooks/useIsAuthenticated';

import type { IHomepageSlot } from 'types';
import { HOMEPAGE_SLOT_TYPES } from 'types';

import styles from './HomepageTemplate.module.scss';

interface Props {
  previewId?: string
}

const HomepageTemplate = (props: Props) => {
  const {
    previewId,
  } = props;

  // Refs

  const initialLoadingRef = React.useRef(true);

  // Hooks

  const {
    // isFetching: isAuthenticationFetching,
    isSettled: isAuthenticationSettled,
    isAuthenticated,
  } = useIsAuthenticated();

  const {
    isFetching,
    data
  } = useHomepage({
    isPersonalized: isAuthenticated,
    previewId
  }, {
    placeholderData: keepPreviousData,
  });

  // Effects

  React.useEffect(() => {
    if (!isAuthenticationSettled) {
      initialLoadingRef.current = true;
    }

    if (isAuthenticationSettled && !isFetching) {
      initialLoadingRef.current = false;
    }
  }, [isAuthenticationSettled, isFetching]);

  // Props

  const {
    slots = []
  } = data || {};

  const {
    intro: introSlots,
    main: mainSlots
  } = React.useMemo(
    () => parseSlots(slots),
    [slots]
  );

  // Loading State

  // TODO: enable once BE optimization on /homepage-web are done
  // const isLoading = (
  //   isAuthenticationSettled
  //     ? initialLoadingRef.current
  //     : (isAuthenticationFetching || isFetching)
  // );

  // Render

  return (
    <>

      {/* Intro */}
      <Responsive
        breakpoint="lg"
        strict
        desktop={(
          <HomepageIntroDesktop
            slots={introSlots}
            // loading={isLoading}
          />
        )}
        mobile={(
          <HomepageIntroMobile
            slots={introSlots}
            // loading={isLoading}
          />
        )}
      />

      {/* Main Slots */}
      <div className={styles.slots}>
        <HomepageSlots slots={mainSlots} />
      </div>

      {/* Order Feedback Reminder Modal Manager */}
      <OrderFeedbackReminderModalManager />

      {/* Snowfall */}
      <Snowfall />
    </>
  );
};

// Helpers

const {
  BANNERS_CAROUSEL,
  PROMOTIONS_CAROUSEL,
  NAVIGATION
} = HOMEPAGE_SLOT_TYPES;

const parseSlots = (slots: IHomepageSlot[]): {
  intro: {
    [BANNERS_CAROUSEL]: IHomepageSlot,
    [PROMOTIONS_CAROUSEL]: IHomepageSlot
  },
  main: IHomepageSlot[]
} => {
  return slots.reduce((acc, slot) => {
    const { type } = slot || {};

    if (type === BANNERS_CAROUSEL && !acc.intro[BANNERS_CAROUSEL]) {
      acc.intro[BANNERS_CAROUSEL] = slot;
    } else if (type === PROMOTIONS_CAROUSEL && !acc.intro[PROMOTIONS_CAROUSEL]) {
      acc.intro[PROMOTIONS_CAROUSEL] = slot;
    } else if (type === NAVIGATION && !acc.intro[NAVIGATION]) {
      acc.intro[NAVIGATION] = slot;
    } else {
      acc.main.push(slot);
    }

    return acc;

  }, {
    intro: {
      [BANNERS_CAROUSEL]: null,
      [PROMOTIONS_CAROUSEL]: null,
      [NAVIGATION]: null,
    },
    main: []
  });
};

// Export

export default HomepageTemplate;
